import { Box, IconButton, SxProps, Tooltip } from '@mui/material';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { type Layout, ReactGridLayout, useContainerWidth } from 'react-grid-layout';
import RefreshIcon from '@mui/icons-material/Refresh';

type GridConfig = {
    /** Number of columns in the grid (default: 12) */
    cols: number;
    /** Height of a single row in pixels (default: 150) */
    rowHeight: number;
    /** [horizontal, vertical] margin between items in pixels (default: [10, 10]) */
    margin: readonly [number, number];
    /** [horizontal, vertical] padding inside the container (default: null, uses margin) */
    containerPadding: readonly [number, number] | null;
    /** Maximum number of rows (default: Infinity) */
    maxRows: number;
};

type StoredDashboard = {
    layout: Layout;
    /** ISO date string of when this layout was last saved */
    savedAt: string;
};

type ReactGridContainerProps = {
    localStorageName: string;
    layoutProps: Layout;
    gridconfig?: Partial<GridConfig>;
    dragConfig?: {
        handle?: string;
        cancel?: string;
        enabled?: boolean;
        bounded?: boolean;
        threshold?: number;
    };
    currentXIndexCard?: React.MutableRefObject<number>;
    currentYIndexCard?: React.MutableRefObject<number>;
    initialXIndexCard?: number;
    showResetButton?: boolean;
    sxWrapper?: SxProps;
    /**
     * ISO date string. If the stored layout has no date (old format) or was saved
     * before this date, localStorage is cleared and the layout resets to layoutProps.
     * Update this value to force all users to reset their dashboard.
     */
    forceResetDate?: string;
};

/**
 *
 * exemple layout
 * const layout: Layout = [
 *      { i: 'a', x: 0, y: 0, w: 1, h: 1, isResizable: true, resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] },
 *      { i: 'd', x: 1, y: 3, w: 1, h: 4, isResizable: true, resizeHandles: ['s'] },
 *      { i: 'b', x: 2, y: 1, w: 1, h: 2, isResizable: false},
 *      { i: 'c', x: 3, y: 0, w: 1, h: 2 },
 *  ];
 * @param param0
 */

// Compare only x/y/w/h — avoids false positives from react-grid-layout's extra fields
// (moved, static) and JSON key-ordering differences between our objects and RGL's output.
const isSameLayout = (a: Layout | undefined, b: Layout): boolean => {
    if (!a || a.length !== b.length) return false;
    return b.every((itemB) => {
        const itemA = a.find((i) => i.i === itemB.i);
        return (
            itemA &&
            itemA.x === itemB.x &&
            itemA.y === itemB.y &&
            itemA.w === itemB.w &&
            itemA.h === itemB.h
        );
    });
};

const localISOString = (): string => {
    const now = new Date();
    // Shift by timezone offset to get local time in ISO format (no trailing Z)
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, -1);
};

const saveToLocalStorage = (key: string, layoutToSave: Layout) => {
    const data: StoredDashboard = {
        layout: layoutToSave,
        savedAt: localISOString(),
    };
    localStorage.setItem(key, JSON.stringify(data));
};

const parseStoredDashboard = (raw: string): StoredDashboard | null => {
    try {
        const parsed = JSON.parse(raw);
        // Old format was a plain array — no savedAt, treat as missing date
        if (Array.isArray(parsed)) return { layout: parsed, savedAt: '' };
        if (parsed && Array.isArray(parsed.layout) && parsed.savedAt) return parsed;
    } catch {
        // ignore invalid JSON
    }
    return null;
};

const ReactGridContainer: React.FunctionComponent<PropsWithChildren<ReactGridContainerProps>> =
    function ({
        localStorageName,
        layoutProps,
        gridconfig = { cols: 6, rowHeight: 40 },
        dragConfig,
        currentXIndexCard,
        initialXIndexCard = -1,
        currentYIndexCard,
        showResetButton = true,
        children,
        sxWrapper,
        forceResetDate,
    }) {
        const { width, containerRef, mounted } = useContainerWidth();
        const [layout, setLayout] = useState<Layout>();

        // Check if layout is stored in the local storage on mount
        useEffect(() => {
            if (layoutProps && layoutProps.length > 0) {
                const raw = localStorage.getItem(localStorageName);
                const stored = raw !== null ? parseStoredDashboard(raw) : null;

                // Reset condition: Pas de date (null ou ancien format) OU sauvegarde avant forceResetDate
                const needsReset =
                    stored === null ||
                    !stored.savedAt ||
                    (!!forceResetDate && stored.savedAt < forceResetDate);

                if (needsReset) {
                    saveToLocalStorage(localStorageName, layoutProps);
                    setLayout(layoutProps);
                } else {
                    // Merge stored positions with current layoutProps to pick up new properties
                    // (e.g. resizeHandles added after the layout was first saved, or new items added).
                    // Filter out orphan items (stored items no longer in layoutProps) to prevent
                    // react-grid-layout from trying to place children-less layout items.
                    const merged = stored.layout
                        .filter((item) => layoutProps.some((l) => l.i === item.i))
                        .map((item) => {
                            const initial = layoutProps.find((l) => l.i === item.i);
                            return initial
                                ? {
                                      ...item,
                                      resizeHandles: initial.resizeHandles,
                                      isResizable: initial.isResizable,
                                  }
                                : item;
                        });
                    const missingItems = layoutProps.filter(
                        (l) => !stored.layout.some((s) => s.i === l.i),
                    );
                    setLayout([...merged, ...missingItems]);
                }
            }
            if (currentXIndexCard && currentXIndexCard.current) {
                // eslint-disable-next-line no-param-reassign
                currentXIndexCard.current = initialXIndexCard;
            }

            if (currentYIndexCard && currentYIndexCard.current) {
                // eslint-disable-next-line no-param-reassign
                currentYIndexCard.current = 0;
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [localStorageName, layoutProps]);

        // On change store the new layout in localStorage and track in state.
        // Only update when positions actually changed — react-grid-layout fires onLayoutChange
        // on every render (same positions, different key order / extra fields), which would
        // otherwise cause an infinite setState loop.
        const handleLayoutChange = (layoutParam: Layout) => {
            if (!layoutParam) return;
            if (!isSameLayout(layout, layoutParam)) {
                saveToLocalStorage(localStorageName, layoutParam);
                setLayout(layoutParam);
            }
        };

        const handleResetLayout = useCallback(() => {
            if (layoutProps && layoutProps.length > 0) {
                saveToLocalStorage(localStorageName, layoutProps);
                setLayout([...layoutProps]);
            }
        }, [layoutProps, localStorageName]);

        return (
            <Box
                sx={{ width: '100%', position: 'relative', padding: '16px 0px', ...sxWrapper }}
                ref={containerRef}
            >
                {showResetButton && (
                    <Tooltip title="Réinitialiser">
                        <IconButton
                            onClick={handleResetLayout}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                zIndex: 10,
                            }}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {mounted && layout && layout.length === layoutProps.length && (
                    <ReactGridLayout
                        layout={layout}
                        width={width}
                        gridConfig={gridconfig}
                        dragConfig={dragConfig}
                        onLayoutChange={handleLayoutChange}
                    >
                        {children}
                    </ReactGridLayout>
                )}
            </Box>
        );
    };

export default ReactGridContainer;
